/**
 * Clamp QC hub inspection line-reading rules for UI submit and API persist.
 * When mechanical or inductance fails first, line harvest is not required — persist null, not zero or partial.
 */

export function parseClampQcOptionalLineReading(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return null;
  }
  const parsed = Number.parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

export function clampQcInspectionLineReadingsBothAbsent(
  unloaded: number | null,
  loaded: number | null,
): boolean {
  return unloaded === null && loaded === null;
}

export function clampQcInspectionLineReadingsPairComplete(
  unloaded: number | null,
  loaded: number | null,
): boolean {
  return unloaded !== null && loaded !== null;
}

/** Mechanical fail is first in the QC sequence — inductance and line harvest are not required. */
export function clampQcInspectionMechanicalFailSkipsOtherTests(
  physicalFailReasonLabels: readonly string[],
): boolean {
  return physicalFailReasonLabels.length > 0;
}

/** Inductance fail (when sampled) skips line harvest; mechanical fail already skips both. */
export function clampQcInspectionLineHarvestSkippedBecauseEarlierFail(input: {
  physicalFailReasonLabels: readonly string[];
  sgsInductancePass: boolean | null;
}): boolean {
  if (clampQcInspectionMechanicalFailSkipsOtherTests(input.physicalFailReasonLabels)) {
    return true;
  }
  return input.sgsInductancePass === false;
}

export function clampQcInspectionLineHarvestRequiredForPersist(input: {
  physicalFailReasonLabels: readonly string[];
  sgsInductancePass: boolean | null;
}): boolean {
  return !clampQcInspectionLineHarvestSkippedBecauseEarlierFail(input);
}

export function clampQcInspectionLineHarvestRequiredForUiSubmit(input: {
  physicalFailReasonLabels: readonly string[];
  inductanceSampled: boolean;
  inductanceOutOfBand: boolean;
}): boolean {
  if (clampQcInspectionMechanicalFailSkipsOtherTests(input.physicalFailReasonLabels)) {
    return false;
  }
  if (input.inductanceSampled && input.inductanceOutOfBand) {
    return false;
  }
  return true;
}

/**
 * True when drag line readings may be omitted on persist (unit was not drag-tested).
 */
export function clampQcInspectionAllowsAbsentLineReadings(input: {
  physicalFailReasonLabels: readonly string[];
  sgsLineHarvestPass: boolean | null;
  sgsInductancePass: boolean | null;
}): boolean {
  if (clampQcInspectionLineHarvestSkippedBecauseEarlierFail(input)) {
    return true;
  }
  if (input.sgsLineHarvestPass === false) {
    return true;
  }
  return false;
}

export type ClampQcInspectionLineReadingsPersistValidation =
  | { ok: true }
  | { ok: false; error: string };

export function validateClampQcInspectionLineReadingsForPersist(input: {
  unloaded: number | null;
  loaded: number | null;
  physicalFailReasonLabels: readonly string[];
  sgsLineHarvestPass: boolean | null;
  sgsInductancePass: boolean | null;
}): ClampQcInspectionLineReadingsPersistValidation {
  if (
    !clampQcInspectionLineHarvestRequiredForPersist({
      physicalFailReasonLabels: input.physicalFailReasonLabels,
      sgsInductancePass: input.sgsInductancePass,
    })
  ) {
    return { ok: true };
  }

  const bothAbsent = clampQcInspectionLineReadingsBothAbsent(input.unloaded, input.loaded);
  const bothPresent = clampQcInspectionLineReadingsPairComplete(input.unloaded, input.loaded);
  if (!bothAbsent && !bothPresent) {
    return {
      ok: false,
      error:
        "sgsUnloadedLine and sgsLoadedLineBeforeTester must both be numbers when line harvest is required.",
    };
  }
  if (bothAbsent && !clampQcInspectionAllowsAbsentLineReadings(input)) {
    return {
      ok: false,
      error: "sgsUnloadedLine and sgsLoadedLineBeforeTester must be numbers for hub inspection rows.",
    };
  }
  return { ok: true };
}

export type ClampQcInspectionLineReadingsUiSubmitResult =
  | { ok: true; unloaded: number | null; loaded: number | null }
  | { ok: false; error: string };

/**
 * UI submit guard: when line harvest is not required, persist null readings regardless of field contents.
 */
export function validateClampQcInspectionLineReadingsForUiSubmit(input: {
  unloadedRaw: string;
  loadedRaw: string;
  lineHarvestRequired: boolean;
}): ClampQcInspectionLineReadingsUiSubmitResult {
  if (!input.lineHarvestRequired) {
    return { ok: true, unloaded: null, loaded: null };
  }

  const unloaded = parseClampQcOptionalLineReading(input.unloadedRaw);
  const loaded = parseClampQcOptionalLineReading(input.loadedRaw);
  const bothAbsent = clampQcInspectionLineReadingsBothAbsent(unloaded, loaded);
  const bothPresent = clampQcInspectionLineReadingsPairComplete(unloaded, loaded);
  if (!bothAbsent && !bothPresent) {
    return {
      ok: false,
      error: "Enter unloaded line and loaded line before tester as numbers.",
    };
  }
  if (bothAbsent) {
    return {
      ok: false,
      error: "Enter unloaded line and loaded line before tester as numbers so drag can be saved with this inspection.",
    };
  }
  return { ok: true, unloaded, loaded };
}
